import * as ngCore from '@angular/core'

import * as directives from '../../../directives'

/**
 * The Page Layout component is a versatile UI element designed to display a page with a
 * header and an aside section. It leverages content projection slots to position various
 * elements on the page, providing flexibility and customization options.
 *
 * **Slots**
 * - `pageHeader`: Present at the top of the page.
 * - `pageAside`: Present on the left side of the page.
 * - *`default`*: Present in the body of the page.
 *
 * @example
 * ```html
 * <atn-ds-page>
 *  <div pageHeader>header content</div>
 *  <div pageAside>aside content</div>
 *  <div>Page content</div>
 * </atn-ds-page>
 * ```
 */
@ngCore.Component({
  imports: [directives.measuredElement.Directive],
  selector: 'atn-ds-page',
  standalone: true,
  styleUrls: ['./page.component.scss'],
  templateUrl: './page.component.html',
})
export class AtnDsPageComponent {
  /**
   * Whether the page content should be scrollable.
   *
   * @default false
   */
  public scrollableSg = ngCore.input(false, { alias: 'scrollable' })

  /** @see {@link directives.measuredElement.Directive} - attached to header element */
  private readonly _measuredElementSg =
    ngCore.viewChild<directives.measuredElement.Directive>(
      directives.measuredElement.Directive,
    )

  /**
   * The final height of the page header after rendering the pageHeader content slot.
   *
   * @default 0
   */
  private readonly _pageHeaderHeightSg = ngCore.computed(
    (measuredElement = this._measuredElementSg()) =>
      measuredElement?.heightSg() ?? 0,
  )

  /** @see {@link ngCore.ElementRef<HTMLElement>} */
  private readonly _pageAsideElSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLElement>>('pageAside')
  /** @see {@link ngCore.ElementRef<HTMLElement>} */

  /** Whether the page aside slot has content. */
  public readonly pageAsideExistsSg = ngCore.computed(() => {
    const el = this._pageAsideElSg()
    return el ? el?.nativeElement?.children?.length > 0 : false
  })

  /**
   * Styles for the body of the page.
   *
   * Depending on the presence of a page header, the body will have a different height.
   */
  public readonly bodyStylesSg = ngCore.computed<Partial<CSSStyleDeclaration>>(
    (pageHeaderHeight = this._pageHeaderHeightSg()) => ({
      // max-height is essential to prevent the body content from exceeding
      // 100% of the body height when enabling scrolling on the page
      maxHeight: `calc(100% - ${pageHeaderHeight}px)`,
    }),
  )
}
